import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import PositiveOrNegetiveSTSkeleton from "../../../../../SearchTermSkeletons/PositiveOrNegetiveSTSkeleton";
import {
  setAllDefinitaions,
  removeDefinition,
} from "../../../../../../../../../features/aiRecomendationTabSlice";
import { useDispatch } from "react-redux";

const definitionOptions = [
  { label: "Remove Negative", value: "mark-as-removed" },
  { label: "Ignore", value: "mark-as-ignored" },
];

const AiRecomondationST = ({ open, rowData, onClose }: any) => {
  const {
    positiveSTLoader,
    keywordRecommendations,
    allDefinitaions,
    allExactST,
  } = useSelector((state: any) => state.aiRecomandationTab);
  const dispatch = useDispatch();

  // Get recommendations for the specific keyword
  const currentKeywordData = rowData?.keyword_shared_uid
    ? keywordRecommendations[rowData.keyword_shared_uid]
    : null;

  // Filter recommendations to exclude search terms from allExactST
  const filteredRecommendations = useMemo(() => {
    if (!currentKeywordData?.recommendations || !allExactST) {
      return currentKeywordData?.recommendations || [];
    }

    // Create a Set of composite keys: "search_term|match_type" for fast lookup
    const searchTermsSet = new Set(
      allExactST.map((item: any) => {
        const term = item.search_term?.toLowerCase().trim() || "";
        const matchType =
          item.search_term_match_type?.toLowerCase().trim() || "";
        return `${term}|${matchType}`;
      })
    );

    // Filter out recommendations where negative_term + match_type matches any search term
    return currentKeywordData.recommendations.filter((rec: any) => {
      const negativeTerm = rec.negative_term?.toLowerCase().trim() || "";
      const matchType = rec.match_type?.toLowerCase().trim() || "";
      const compositeKey = `${negativeTerm}|${matchType}`;

      return !searchTermsSet.has(compositeKey);
    });
  }, [currentKeywordData?.recommendations, allExactST]);

  // --- Define columns with correct field mappings ---
  const columns: GridColDef[] = [
    {
      field: "negative_term",
      headerName: "Negative Search Term",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "match_type",
      headerName: "Match Type",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "negative_level",
      headerName: "Negative Level",
      flex: 0.8,
      minWidth: 140,
    },
    {
      field: "similar_positive_term",
      headerName: "Positive Search Term",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "similarity_score",
      headerName: "Estimated Match Relevance",
      flex: 0.8,
      minWidth: 180,
      valueGetter: (value) => `${value}%`,
    },
    {
      field: "Definition",
      headerName: "Definition",
      flex: 0.8,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => {
        // Create a composite key: keyword_shared_uid + recommendation_shared_uid
        const recommendationSharedUid = params.row.shared_uid || params.row.id;
        const compositeKey = `${rowData?.keyword_shared_uid}_${recommendationSharedUid}`;

        // Get the current selected value from Redux state using composite key
        const selectedDefinition = allDefinitaions[compositeKey]?.definition;
        const selectedOption = definitionOptions.find(
          (option) => option.value === selectedDefinition
        );

        return (
          <Autocomplete
            size="small"
            options={definitionOptions}
            getOptionLabel={(option) => option.label}
            value={selectedOption || null} // Controlled value from Redux
            onChange={(_, selectedOption) => {
              if (selectedOption) {
                // Set the definition when an option is selected
                dispatch(
                  setAllDefinitaions({
                    id: compositeKey, // Use composite key
                    row: {
                      ...params.row,
                      keyword_shared_uid: rowData?.keyword_shared_uid,
                      keyword_name: rowData?.keyword_name,
                    },
                    definition: selectedOption.value,
                  })
                );
              } else {
                // Remove the definition when cleared
                dispatch(removeDefinition(compositeKey));
              }
            }}
            sx={{ width: "100%" }}
            renderInput={(paramsInput) => (
              <TextField
                {...paramsInput}
                placeholder="Select..."
                variant="standard"
                color="secondary"
                InputProps={{
                  ...paramsInput.InputProps,
                  disableUnderline: true,
                }}
                data-tracker="610"
              />
            )}
          />
        );
      },
    },
  ];

  // Add unique id to each row for DataGrid - use filtered recommendations
  const rowsWithId =
    filteredRecommendations?.map((row: any, index: number) => ({
      ...row,
      id: row.shared_uid || index, // Use shared_uid as id
    })) || [];

  if (!rowData) return null;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>{rowData?.keyword_name} - AI Recommendations</DialogTitle>
      <DialogContent>
        {positiveSTLoader ? (
          <PositiveOrNegetiveSTSkeleton />
        ) : (
          <Box sx={{ width: "100%", display: "grid" }}>
            <DataGridPro
              sx={{
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                  { outline: "none" },
                "& .MuiDataGrid-cellEmpty": {
                  width: "auto",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "rgba(156, 39, 176, 0.08) !important",
                  "&:hover": {
                    backgroundColor: "rgba(156, 39, 176, 0.16) !important",
                  },
                },
                "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft":
                  {
                    backgroundColor: "#f0def3 !important",
                  },
                "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedLeft":
                  {
                    backgroundColor: "#e6d6e9ff !important",
                  },
                "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight":
                  {
                    backgroundColor: "#f0def3 !important",
                  },
                "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight":
                  {
                    backgroundColor: "#e6d6e9ff !important",
                  },
                "& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight":
                  {
                    backgroundColor: "#ffffff",
                  },
                "& .MuiDataGrid-cell.Mui-selected": {
                  backgroundColor: "inherit !important",
                },
                "& .MuiCheckbox-root": {
                  color: "#9c27b0 !important",
                },
                "& .MuiCheckbox-root.Mui-checked": {
                  color: "#9c27b0",
                },
                border: "none",
              }}
              density="compact"
              hideFooter
              pagination={false}
              rows={rowsWithId}
              columns={columns}
              disableColumnMenu
              disableColumnFilter
              disableColumnReorder
              sortingOrder={["asc", "desc"]}
              isCellEditable={() => false}
              disableRowSelectionOnClick
              initialState={{
                pinnedColumns: {
                  left: ["negative_term", "match_type"],
                  right: ["Definition"],
                },
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: "0px 20px 16px" }}>
        <Button color="secondary" variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiRecomondationST;
